import { supabase } from './supabase'
import { reportProblem } from './problems'
import type { SpaceKind } from './categories'
import { lastDayOfMonth } from './dates'

/**
 * Spending you have already done, and a space your family may look into.
 *
 * A receipt is not an invoice. There is nothing left to plan, no account
 * number to check, no QR code to make — the money is gone. In the ledger that
 * is an obligation with one instalment and a payment already on it, written in
 * a single transaction so an expense never exists in a state where it looks
 * unpaid.
 */

export interface SharedSpace {
  id: string
  name: string
  kind: SpaceKind
  membersSeeMoney: boolean
}

export async function createSpace(params: {
  name: string
  kind: SpaceKind
  membersSeeMoney: boolean
}): Promise<string> {
  const { data, error } = await supabase.rpc('create_space', {
    p_name: params.name,
    p_kind: params.kind,
    p_members_see_money: params.membersSeeMoney,
  })
  if (error) throw new Error(friendly(error.message))
  return data as string
}

export async function setSpaceShared(spaceId: string, shared: boolean): Promise<void> {
  const { error } = await supabase
    .from('spaces')
    .update({ members_see_money: shared })
    .eq('id', spaceId)
  if (error) throw error
}

export async function loadSharedSpaces(): Promise<SharedSpace[]> {
  const { data, error } = await supabase
    .from('spaces')
    .select('id, name, kind, members_see_money')
    .eq('members_see_money', true)
    .is('deleted_at', null)
    .order('name')
  if (error) throw error

  return (data ?? []).map((row) => ({
    id: row.id as string,
    name: row.name as string,
    kind: row.kind as SpaceKind,
    membersSeeMoney: Boolean(row.members_see_money),
  }))
}

export async function recordExpense(params: {
  spaceId: string
  description: string
  amountCents: number
  spentOn: string
  category: string | null
  documentId: string | null
  /**
   * The idempotency key of a receipt photo still in the upload queue. The
   * expense is saved now and the photo is linked whenever it lands — in either
   * order, since the queue can beat the form. See 0032.
   */
  receiptKey?: string | null
}): Promise<string> {
  const { data, error } = await supabase.rpc('record_expense', {
    p_space_id: params.spaceId,
    p_description: params.description,
    p_amount_cents: params.amountCents,
    p_spent_on: params.spentOn,
    p_category: params.category,
    p_document_id: params.documentId,
    p_receipt_key: params.receiptKey ?? null,
  })
  if (error) throw new Error(friendly(error.message))
  return data as string
}

export async function monthTotal(spaceId: string, month: string): Promise<number> {
  const { data, error } = await supabase.rpc('space_month_total', {
    p_space_id: spaceId,
    p_month: month,
  })
  if (error) throw error
  return Number(data ?? 0)
}

/**
 * Correcting a typed number.
 *
 * An expense is created with a payment already on it, and 0017 protects
 * anything with a payment from being thrown away — so until 0033 a mistyped
 * amount stayed forever, and kept counting in the monthly total your parents
 * see. `correct_expense` and `remove_expense` are the way past that guard, and
 * they only open for expenses: a real paid invoice stays exactly as protected
 * as it was.
 *
 * The old ledger entry is voided, never overwritten (rule 4). What it said is
 * still there afterwards.
 */
export async function correctExpense(params: {
  obligationId: string
  description: string
  amountCents: number
  spentOn: string
  category: string | null
}): Promise<void> {
  const { error } = await supabase.rpc('correct_expense', {
    p_obligation: params.obligationId,
    p_description: params.description,
    p_amount_cents: params.amountCents,
    p_spent_on: params.spentOn,
    p_category: params.category,
  })
  if (error) throw new Error(friendly(error.message))
}

/**
 * The receipt you found later.
 *
 * The usual case, not the exception: you type 12,40 standing in a shop and the
 * paper turns up on the table that evening. The amount is the easy half — you
 * remember it. The receipt is the part that cannot be reconstructed.
 *
 * Refused when one is already attached. Documents are immutable (rule 6) and
 * quietly swapping one piece of proof for another is how an archive stops being
 * worth keeping.
 */
export async function attachReceipt(obligationId: string, receiptKey: string): Promise<void> {
  const { error } = await supabase.rpc('attach_receipt', {
    p_obligation: obligationId,
    p_receipt_key: receiptKey,
  })
  if (error) throw new Error(friendly(error.message))
}

export async function removeExpense(obligationId: string): Promise<void> {
  const { error } = await supabase.rpc('remove_expense', { p_obligation: obligationId })
  if (error) throw new Error(friendly(error.message))
}

export interface Expense {
  obligationId: string
  description: string
  amountCents: number
  spentOn: string
  category: string | null
  documentId: string | null
  /**
   * A photo was taken but has not finished uploading — the phone was offline,
   * or the app was closed before the queue drained. Distinct from having no
   * receipt at all, and worth showing as its own thing: "no proof" and "proof
   * on its way" are different answers to the same question.
   */
  awaitingReceipt: boolean
}

/** What the month was actually spent on. Newest first. */
export async function loadExpenses(spaceId: string, month: string): Promise<Expense[]> {
  const from = `${month}-01`
  const to = lastDayOfMonth(month)

  const { data, error } = await supabase
    .from('obligations')
    .select('id, description, total_amount_cents, due_date, category, source_document_id, awaiting_receipt_key')
    .eq('space_id', spaceId)
    .gte('due_date', from)
    .lte('due_date', to)
    .is('deleted_at', null)
    .order('due_date', { ascending: false })
  if (error) throw error

  return (data ?? []).map((row) => ({
    obligationId: row.id as string,
    description: (row.description as string) ?? 'Uitgave',
    amountCents: Number(row.total_amount_cents),
    spentOn: row.due_date as string,
    category: (row.category as string | null) ?? null,
    documentId: (row.source_document_id as string | null) ?? null,
    awaitingReceipt: Boolean(row.awaiting_receipt_key),
  }))
}

export function thisMonth(today = new Date()): string {
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`
}

/**
 * The three errors a person can actually cause, said in Dutch — and, for
 * everything else, the real message.
 *
 * This used to end at "Dat lukte niet." for anything it did not recognise,
 * which read politely and told nobody anything. When the space button failed
 * for a missing database function, that sentence was all there was to go on:
 * the one fact that would have identified it in a second had been thrown away
 * on the way to the screen.
 *
 * So an unrecognised failure now carries its own cause. It is not pretty, and
 * it is not meant to be — these screens are the administrator's, and a plain
 * error you can read out is worth more than a tidy one you cannot act on. It
 * is also written to `client_errors`, so it shows up under Problemen whether
 * or not anyone thought to write it down.
 */
function friendly(message: string): string {
  if (message.includes('alleen de beheerder')) return 'Alleen jij kan een ruimte maken.'
  if (message.includes('geef de ruimte een naam')) return 'Geef de ruimte een naam.'
  if (message.includes('een uitgave heeft een bedrag')) return 'Vul een bedrag in.'
  if (message.includes('hier zit al een bonnetje bij')) return 'Hier zit al een bonnetje bij.'
  if (message.includes('geen ingetypte uitgave')) return 'Dit is geen uitgave die je zelf hebt ingetypt.'

  void reportProblem(message)

  // The one failure worth naming, because it has a cure and the raw text does
  // not suggest it: the app is newer than the database.
  if (/could not find the function|schema cache|PGRST202/i.test(message)) {
    return 'De databank mist nog een stuk (migratie niet gedraaid). ' + short(message)
  }
  return 'Dat lukte niet — ' + short(message)
}

function short(message: string): string {
  return message.length > 140 ? message.slice(0, 137) + '…' : message
}
